import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HarmonyIcon } from '@rsos/assets/icons';
import { InfoIcon, RewardStarsIcon } from '@rsos/assets/icons';
import Popover from '@rsos/components/capstone/base/Popover';
import {
  PoweredByHarmonyContainer,
  TitleBox,
  TitleText,
  PopoverBox,
  PopoverText,
  HarmonyBox,
  HarmonyText,
  HarmonyLink,
} from './PoweredByHarmony.styles';

// Default popover positions.
const FALLBACK_POPOVER_POSITION = -78;
const POPOVER_GAP = -5;

/**
 * Powered by Harmony component used in IncidentSummary.js and Keywords.js
 * @param {Object} props - Component props
 * @param {boolean} [props.showStar=false] - Flag for displaying the RewardStarsIcon
 * @param {string} [props.title=''] - Header text
 */
const PoweredByHarmony = ({ showStar = false, title = '' }) => {
  const popoverRef = useRef();

  const [popoverBoundingClient, setPopoverBoundingClient] = useState(null);

  const [isPopoverVisible, setIsPopoverVisible] = useState(false);

  const popoverPosition = useMemo(() => {
    let position = FALLBACK_POPOVER_POSITION;

    if (popoverBoundingClient?.height) {
      position = -popoverBoundingClient.height;
    }

    return position + POPOVER_GAP;
  }, [popoverBoundingClient]);

  const handleInfoIconHover = useCallback(
    isVisible => setIsPopoverVisible(isVisible),
    [],
  );

  useEffect(() => {
    const popoverRect = popoverRef?.current?.getBoundingClientRect();

    setPopoverBoundingClient(popoverRect);
  }, [isPopoverVisible]);

  return (
    <PoweredByHarmonyContainer>
      <TitleBox>
        {showStar && (
          <div data-name="rewards-star-icon">
            <RewardStarsIcon color="primaryText" height={14} width={14} />
          </div>
        )}
        <TitleText data-name="powered-by-harmony-title">{title}</TitleText>
        <div
          data-name="info-icon"
          onMouseEnter={() => handleInfoIconHover(true)}
          onMouseLeave={() => handleInfoIconHover(false)}
        >
          <Popover
            cssDisplayProperty="block"
            data-name="powered-by-harmony-popover"
            isOpen={isPopoverVisible}
            popoverID="powered-by-harmony-popover"
            trigger={<InfoIcon color="secondaryText" width={14} height={14} />}
            useCenterXOffset
            useCustomOffset
            yOffset={popoverPosition}
          >
            <PopoverBox ref={popoverRef}>
              <PopoverText data-name="powered-by-harmony-popover-text">
                <strong>Harmony is Non Authoritative</strong>: This summary is
                AI-generated and should be used supplementary to verified
                emergency information.
              </PopoverText>
            </PopoverBox>
          </Popover>
        </div>
      </TitleBox>
      <HarmonyBox data-name="powered-by-harmony">
        <HarmonyText>
          Powered by{' '}
          <HarmonyLink to="https://rapidsos.com/harmony/">Harmony</HarmonyLink>
        </HarmonyText>
        <HarmonyIcon color="secondaryText" height="14" width="14"></HarmonyIcon>
      </HarmonyBox>
    </PoweredByHarmonyContainer>
  );
};

export default PoweredByHarmony;
